import { LearningSet } from "../../../interfaces/AthenaResources";
import { joinPath } from "../../../utils/path";
import { isAudio, isImage } from "./fileUtils";

export default function getAssetUrls(
    learningSetRecord: { [learningSetId: string]: LearningSet },
    appVersion: string,
    baseAssetsUrl: string
): string[] {
    const assetPaths: Set<string> = new Set();
    for (const learningSet of Object.values(learningSetRecord)) {
        if ("learning_item_data" in learningSet) {
            extractAssetPathsAndAddToSet(
                learningSet.learning_item_data,
                assetPaths
            );
        }
    }
    return [...assetPaths].map((assetPath) =>
        makeAssetUrl(assetPath, baseAssetsUrl, appVersion)
    );
}

/**
 * This function extracts all asset paths referenced in the passed in
 * learningItemData object and adds them to the assetPaths set passed in as
 * second parameter.
 *
 * Note: the shape of the learningItemData object is not known, in the sense
 * that it changes from learning item to learning item, and we don't have a type
 * that describes all possible learningItemData shapes. Therefore, the function
 * treats it as a black-box object, and just recursively goes through its
 * properties and tests whether they are paths or not, and based on that adds
 * them to the set.
 */
function extractAssetPathsAndAddToSet(
    learningItemDataObject: any,
    assetPaths: Set<string> = new Set(),
    /**
     * [TEMP]
     * Currently the frontend is in charge of determining whether a file is
     * located in the /tss or /content folder. This causes a pb because some
     * audio files are not in /tts. The current strategy, waiting for the
     * backend to give the complete path to the files, is to look at whether the
     * object containing the string for the audio has a isTts key
     */
    isTts: boolean = false
): Set<string> {
    if (
        learningItemDataObject &&
        typeof learningItemDataObject === "string" &&
        (isAudio(learningItemDataObject, true) ||
            isImage(learningItemDataObject, true))
    ) {
        assetPaths.add(prepareAssetPath(learningItemDataObject, isTts));
    } else if (Array.isArray(learningItemDataObject)) {
        learningItemDataObject.forEach((jsonValue) => {
            extractAssetPathsAndAddToSet(jsonValue, assetPaths);
        });
    } else if (
        learningItemDataObject &&
        typeof learningItemDataObject === "object"
    ) {
        Object.values(learningItemDataObject).forEach((value) => {
            extractAssetPathsAndAddToSet(
                value,
                assetPaths,
                "isTts" in learningItemDataObject &&
                    !!learningItemDataObject.isTts
            );
        });
    }
    return assetPaths;
}

const prepareAssetPath = (asset: string, isTts: boolean): string => {
    if (isAudio(asset, true) && isTts) {
        return joinPath("tts", asset);
    } else {
        return joinPath("content", asset);
    }
};

export const makeAssetUrl = (
    assetUrl: string,
    baseAssetsUrl: string,
    appVersion: string
): string => {
    const versionParam = `?version=${appVersion}`;
    if (assetUrl.startsWith("http")) {
        return assetUrl + versionParam;
    } else {
        return joinPath(baseAssetsUrl, assetUrl) + versionParam;
    }
};
